import { useState, useEffect, useRef } from 'react';
import styles from './multi-select-box.module.scss';

const MultiSelectBox = ({ label, list, value, required, onChange }) => {
    const [kwargs, setKwargs] = useState('');
    const [selectedScopes, setSelectedScopes] = useState(value || {}); // Controlled by parent
    const [filteredScopes, setFilteredScopes] = useState(list || {});
    const [displayTooltip, setDisplayTooltip] = useState(false);
    const tooltipRef = useRef(null);
    const [clickedScope, setClickedScope] = useState('');

    // Sync with external value only when it actually changes
    useEffect(() => {
        if (JSON.stringify(value) !== JSON.stringify(selectedScopes)) {
            setSelectedScopes(value || {});
        }
    // eslint-disable-next-line
    }, [value]);

    useEffect(() => {
        setFilteredScopes(list);
    }, [list]);

    useEffect(() => {
        if (!kwargs.trim()) {
            setFilteredScopes(list);
        } else {
            const newFilteredScopes = Object.keys(list).reduce((acc, key) => {
                const filtered = list[key].scopes.filter(scope =>
                    scope.toLowerCase().includes(kwargs.toLowerCase())
                );

                if (filtered.length > 0) {
                    acc[key] = { ...list[key], scopes: filtered };
                }
                return acc;
            }, {});

            setFilteredScopes(newFilteredScopes);
        }
    }, [kwargs, list]);

    useEffect(() => {
        if (JSON.stringify(selectedScopes) !== JSON.stringify(value)) {
            onChange(selectedScopes);
        }
    // eslint-disable-next-line
    }, [selectedScopes]);

    const handleCheckboxChange = (category, scope) => {
        setSelectedScopes((prev) => {
            const currentScopes = prev[category]?.scopes || [];
            const updatedScopes = currentScopes.includes(scope)
                ? currentScopes.filter((s) => s !== scope) // Remove if already selected
                : [...currentScopes, scope]; // Add if not selected
    
            // If category is empty, remove it from selectedScopes
            const newScopes = { ...prev };
            if (updatedScopes.length === 0) {
                delete newScopes[category];
            } else {
                // Preserve title from `list`
                newScopes[category] = {
                    title: list[category].title, // Include title
                    scopes: updatedScopes,
                };
            }

            return sortKeysExceptJanitorial(newScopes);
        });
    };
    
    const handleTooltip = (scope) => {
        setClickedScope(scope);
        setDisplayTooltip(true);

        // Hide tooltip after 3 seconds (adjust time as needed)
        setTimeout(() => {
            setDisplayTooltip(false);
        }, 2000);
    };

    // This will update the text AFTER the tooltip is shown
    useEffect(() => {
        if (displayTooltip && tooltipRef.current) {
            tooltipRef.current.innerText = clickedScope;
        }
    }, [displayTooltip, clickedScope]); 

    return (
        <div className={styles.container}>
            <p>
                {required ? '*' : null}
                <b>{label}</b>
            </p>
            <div className={styles.searchBox}>
                <input
                    placeholder="Search"
                    value={kwargs}
                    onChange={(e) => setKwargs(e.target.value)}
                />
            </div>
            <div className={styles.listWrapper}>
                {Object.entries(filteredScopes).map(([key, value]) => (
                    <div key={key}>
                        <h3>{value.title}</h3>
                        <div>
                            {value.scopes.map((scope) => (
                                <div key={scope} className={styles.listItem}>
                                    <input
                                        type="checkbox"
                                        checked={selectedScopes[key]?.["scopes"].includes(scope) || false}
                                        onChange={() => handleCheckboxChange(key, scope)}
                                    />
                                    <p onClick={() => handleTooltip(scope)}>{scope}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            {displayTooltip ?
                <div className={styles.tooltip}>
                    <p ref={tooltipRef}></p>
                </div>
                :
                null
            }
        </div>

    );
};

export default MultiSelectBox;

const sortKeysExceptJanitorial = (obj) => {
    const keys = Object.keys(obj);
    
    // Separate "janitorial" if it exists
    const janitorialIndex = keys.indexOf("janitorial");
    const janitorialEntry = janitorialIndex !== -1 ? { janitorial: obj["janitorial"] } : null;

    // Sort the remaining keys alphabetically
    const sortedKeys = keys.filter(key => key !== "janitorial").sort();

    // Construct the sorted object
    const sortedObj = {};
    sortedKeys.forEach(key => {
        sortedObj[key] = obj[key];
    });

    // Add "janitorial" back at the end if it exists
    if (janitorialEntry) {
        sortedObj["janitorial"] = janitorialEntry["janitorial"];
    }

    return sortedObj;
};